// ** react imports
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// ** custom hooks imports
import { useUploadTemplate } from '../../../hooks/apis/useQuizTemplate';

// ** components imports
import NormalButton from '../../../components/common/Buttons/NormalButton';

// ** enums imports
import { BUTTON_THEMES, USER_ROLES } from '../../../enums/common';
import { toast } from 'react-toastify';

const AddQuiz = props => {
  // props
  const {} = props;

  // hooks
  const navigate = useNavigate();
  const { token, users } = useSelector(state => state.users);

  // states
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  // custom hooks
  const { uploadTemplate, isUploadTemplateLoading, uploadTemplateError } =
    useUploadTemplate();

  // File upload handlers
  const handleFileSelect = event => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/json') {
        setSelectedFile(file);

        // Preview file content
        const reader = new FileReader();
        reader.onload = e => {
          try {
            const content = JSON.parse(e.target.result);
            setFilePreview(content);
          } catch (error) {
            setFilePreview({ error: 'Invalid JSON file' });
          }
        };
        reader.readAsText(file);
      } else {
        setSelectedFile(null);
        setFilePreview(null);
        toast.error('Please select a valid JSON file');
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  // Form submission handlers
  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      toast.error('No file selected');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const result = await uploadTemplate(formData);

      if (result.data) {
        navigate('/quiz/list');
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  // Check if user is admin
  if (!token || users?.role !== USER_ROLES.ADMIN) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Only administrators can access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full mx-auto">
        {/* Page header */}
        <div className="sm:flex sm:justify-between sm:items-center mb-6">
          {/* Left: Title */}
          <div className="mb-4 sm:mb-0">
            <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
              Upload Quiz Template
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Upload a JSON file to create a new quiz template
            </p>
          </div>
        </div>

        {/* Page content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-6">
            {/* Upload JSON Section */}
            <div className="space-y-6">
              {/* File Upload Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                  Upload JSON Template File
                </h3>

                <div className="w-full flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                  {!selectedFile ? (
                    <div>
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="mt-4">
                        <label htmlFor="file-upload" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-gray-100">
                            <span className="text-blue-600 hover:text-blue-500">
                              Upload a JSON file
                            </span>{' '}
                            or drag and drop
                          </span>
                          <span className="mt-1 block text-xs text-gray-500">
                            JSON files only
                          </span>
                        </label>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          accept=".json"
                          className="sr-only"
                          onChange={handleFileSelect}
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-center">
                        <svg
                          className="h-8 w-8 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {selectedFile.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        className="mt-2 text-sm text-red-600 hover:text-red-500"
                      >
                        Remove file
                      </button>
                    </div>
                  )}
                </div>

                {/* File Preview */}
                {filePreview && !filePreview.error && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      File Preview:
                    </h4>
                    <div className="w-full h-[400px] bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-y-auto">
                      <pre className="text-xs text-gray-600 dark:text-gray-400">
                        {JSON.stringify(filePreview, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {filePreview?.error && (
                  <div className="mt-4 text-red-600 text-sm">
                    {filePreview.error}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <NormalButton
                  type="button"
                  theme={BUTTON_THEMES.DANGER}
                  onClick={() => navigate('/quiz/list')}
                >
                  Cancel
                </NormalButton>
                <NormalButton
                  type="button"
                  theme={BUTTON_THEMES.PRIMARY}
                  disabled={isUploadTemplateLoading || !selectedFile}
                  onClick={handleUploadSubmit}
                >
                  {isUploadTemplateLoading ? 'Uploading...' : 'Upload Template'}
                </NormalButton>
              </div>
            </div>
            {/* Error Display */}
            {uploadTemplateError && (
              <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-600 dark:text-red-400 text-sm">
                  {uploadTemplateError?.data?.message || 'An error occurred'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddQuiz;
